import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, Calendar, Trophy, ChevronRight, Film, Heart, Facebook, Mail, MessageCircle } from "lucide-react";
import { API, FUNCHESTER_LOGO, IMAGES } from "@/config";
import SportsTicker from "@/components/SportsTicker";

export default function HomePage() {
  const [onThisDay, setOnThisDay] = useState([]);
  const [loadingOTD, setLoadingOTD] = useState(true);
  
  useEffect(() => {
    fetchOnThisDay();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch
  
  const fetchOnThisDay = async () => {
    try {
      const res = await axios.get(`${API}/on-this-day`);
      setOnThisDay(res.data);
    } catch (err) {
      console.error("Error fetching on this day:", err);
    } finally {
      setLoadingOTD(false);
    }
  };
  
  const today = new Date();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const todayFormatted = `${monthNames[today.getMonth()]} ${today.getDate()}`;
  
  return (
    <>
      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-overlay" style={{ backgroundImage: `url('${IMAGES.heroBackground}')`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.4, position: 'absolute', inset: 0 }}></div>
        <div className="home-hero-overlay"></div>
        <div className="home-hero-content">
          <img src={FUNCHESTER_LOGO} alt="Funchester TV" className="home-hero-logo" />
          <h1 className="home-hero-title">Funchester <span className="hero-accent">TV</span></h1>
          <p className="home-hero-subtitle">Your Home for Kentucky Wildcats Basketball</p>
          <div className="hero-cta-row">
            <Link to="/live" className="btn btn-primary" data-testid="hero-watch-live">
              <Play size={18} /> Watch Live
            </Link>
            <Link to="/vault" className="btn btn-outline" data-testid="hero-the-vault">
              <Film size={18} /> The Vault
            </Link>
          </div>
        </div>
      </section>
      
      {/* Sports Ticker */}
      <SportsTicker />
      
      {/* On This Day Section */}
      {!loadingOTD && onThisDay.length > 0 && (
        <section className="on-this-day-section">
          <div className="container">
            <div className="on-this-day-card">
              <div className="on-this-day-header">
                <Calendar size={24} />
                <h2>On This Day - {todayFormatted}</h2>
              </div>
              <div className="on-this-day-games">
                {onThisDay.slice(0, 3).map((video) => (
                  <Link key={video.id} to={`/watch/${video.id}`} className="on-this-day-game">
                    <div className="otd-game-info">
                      <h3>{video.title}</h3>
                      <p>{video.date} &bull; {video.season_year}</p>
                    </div>
                    <Play size={20} />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* About Section */}
      <section className="home-section">
        <div className="container">
          <div className="home-card about-card">
            <div className="home-card-icon">
              <Trophy size={48} />
            </div>
            <h2 className="home-card-title">About Funchester TV</h2>
            <p className="home-card-text">
              Welcome To Funchester TV! For over 10 Years We Have Provided a Way to Watch Our Beloved 
              Kentucky Wildcats in Action! We Hope to Keep Bringing You Ways To Watch For Years To Come!
            </p>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="home-section">
        <div className="container">
          <div className="features-grid-4">
            <Link to="/live" className="feature-card" data-testid="feature-live">
              <div className="feature-card-image" style={{ backgroundImage: `url('${IMAGES.watchLiveCard}')` }}>
                <div className="feature-card-overlay"></div>
                <div className="live-badge">
                  <span className="live-dot"></span>
                  LIVE
                </div>
              </div>
              <div className="feature-card-content">
                <h3 className="feature-card-title">Watch Live</h3>
                <p className="feature-card-text">Catch Kentucky Wildcats games live!</p>
                <span className="feature-card-link">Watch Now <ChevronRight size={16} /></span>
              </div>
            </Link>
            
            <Link to="/vault" className="feature-card" data-testid="feature-vault">
              <div className="feature-card-image" style={{ backgroundImage: `url('${IMAGES.vaultCard}')` }}>
                <div className="feature-card-overlay"></div>
                <div className="vault-badge">
                  <Film size={16} />
                  ARCHIVE
                </div>
              </div>
              <div className="feature-card-content">
                <h3 className="feature-card-title">The Vault</h3>
                <p className="feature-card-text">Browse games by coaching era from Rupp to Pope.</p>
                <span className="feature-card-link">Explore <ChevronRight size={16} /></span>
              </div>
            </Link>
            
            <Link to="/championships" className="feature-card" data-testid="feature-championships">
              <div className="feature-card-image" style={{ backgroundImage: `url('${IMAGES.championshipsCard}')` }}>
                <div className="feature-card-overlay"></div>
              </div>
              <div className="feature-card-content">
                <h3 className="feature-card-title">8 Championships</h3>
                <p className="feature-card-text">Relive all 8 National Championship victories.</p>
                <span className="feature-card-link">View Gallery <ChevronRight size={16} /></span>
              </div>
            </Link>
            
            <Link to="/donate" className="feature-card" data-testid="feature-donate">
              <div className="feature-card-image" style={{ backgroundImage: `url('${IMAGES.supportUsCard}')` }}>
                <div className="feature-card-overlay"></div>
              </div>
              <div className="feature-card-content">
                <h3 className="feature-card-title">Support Us</h3>
                <p className="feature-card-text">Help us keep bringing you Kentucky basketball!</p>
                <span className="feature-card-link">Donate <ChevronRight size={16} /></span>
              </div>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Community Section */}
      <section className="home-section community-section">
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>Join Our Community</h2>
          <div className="community-grid">
            <a href="https://www.facebook.com/groups/979251609124467" target="_blank" rel="noopener noreferrer" className="community-card facebook-card">
              <Facebook size={40} />
              <h3>Facebook Group</h3>
              <p>Join for news, updates, and discussion about Kentucky sports and upcoming streams.</p>
            </a>
            <a href="https://discord.gg/uWACEZVW5K" target="_blank" rel="noopener noreferrer" className="community-card discord-card" data-testid="discord-link">
              <MessageCircle size={40} />
              <h3>Discord</h3>
              <p>Join for live game chat, real-time UK basketball talk, and more!</p>
            </a>
            <a href="mailto:Funchesterteevee@gmail.com" className="community-card email-card">
              <Mail size={40} />
              <h3>Contact Us</h3>
              <p>Got questions? Reach out to us anytime!</p>
              <span>Funchesterteevee@gmail.com</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
